import { ukDate } from "../lib/dates";
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { HeartHandshake, Calendar, Search } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function Conversions() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/members");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const converts = members.filter((m) => m.is_convert);
  const nonConverts = members.length - converts.length;

  const toggleConvert = async (m) => {
    const payload = { ...m, is_convert: !m.is_convert, convert_date: !m.is_convert ? new Date().toISOString().slice(0, 10) : "" };
    await api.put(`/members/${m.id}`, payload);
    toast.success(!m.is_convert ? "Marked as new believer 🙌" : "Updated");
    load();
  };

  const pieData = [
    { name: "New Believers", value: converts.length },
    { name: "Other Members", value: nonConverts },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Conversions</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Celebrate and shepherd new believers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} p-6 lg:col-span-1 flex flex-col items-center justify-center`} data-testid="conversion-chart">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={3}>
                <Cell fill="#0B5E4E" /><Cell fill="#EBE9E4" />
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBE9E4" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 text-center">
            <div className="font-head text-3xl font-extrabold" style={{ color: "var(--sj-primary)" }}>{converts.length}</div>
            <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Total New Believers</div>
          </div>
        </div>

        <div className={`${CARD} p-6 lg:col-span-2`}>
          <h3 className="font-head text-xl font-semibold mb-4">New Believers</h3>
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="converts-list">
            {converts.length === 0 && <div className="text-center py-8" style={{ color: "var(--sj-text-3)" }}>No conversions recorded yet.</div>}
            {converts.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--sj-bg)" }}>
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: "var(--sj-primary)" }}>{m.first_name[0]}{m.last_name[0]}</div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{m.first_name} {m.last_name}</div>
                  {m.convert_date && <div className="text-xs flex items-center gap-1" style={{ color: "var(--sj-text-2)" }}><Calendar className="h-3 w-3" />Decided on {ukDate(m.convert_date)}</div>}
                </div>
                <button data-testid={`unmark-convert-${m.id}`} onClick={() => toggleConvert(m)} className="text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Remove</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${CARD} p-6`}>
        <h3 className="font-head text-xl font-semibold mb-1">Record a Decision</h3>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Tap a member to mark them as a new believer</p>
        <div className="relative mb-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--sj-primary)" }} />
          <input data-testid="conversion-search" placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] px-4 py-2.5 text-sm pl-10" />
        </div>
        {(() => {
          const eligible = members.filter((m) => !m.is_convert
            && `${m.first_name} ${m.last_name}`.toLowerCase().includes(q.toLowerCase()));
          return (
            <>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Members ({eligible.length})</div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="non-converts-list">
                {eligible.length === 0 && <div className="text-center py-8" style={{ color: "var(--sj-text-3)" }}>{q ? "No members match your search." : "Everyone has been marked as a new believer."}</div>}
                {eligible.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--sj-bg)" }}>
                    <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: "var(--sj-ochre)" }}>{m.first_name[0]}{m.last_name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{m.first_name} {m.last_name}</div>
                    </div>
                    <button data-testid={`mark-convert-${m.id}`} onClick={() => toggleConvert(m)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 text-white hover:brightness-105 transition-all shrink-0" style={{ background: "var(--sj-primary)" }}>
                      <HeartHandshake className="h-4 w-4" /> Mark as new believer
                    </button>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
